import { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Selection = the node the user has clicked in the sidebar tree.
// Drives what the center DetailPane renders.
export type Selection =
  | { type: 'home' }
  | { type: 'my-tasks' }
  | { type: 'uptime' }
  | { type: 'deleted' }
  | { type: 'completed' }
  | { type: 'contacts' }
  | { type: 'pipelines' }
  | { type: 'client'; slug: string }
  | { type: 'project'; slug: string }
  | { type: 'task'; id: string };

interface SelectionState {
  selection: Selection;
  select: (s: Selection) => void;
  expanded: Set<string>;
  toggleExpanded: (key: string) => void;
  isExpanded: (key: string) => boolean;
  expandChain: (keys: string[]) => void;
  activityOpen: boolean;
  toggleActivity: () => void;
  showArchived: boolean;
  toggleShowArchived: () => void;
}

const SelectionContext = createContext<SelectionState | null>(null);

function serialize(s: Selection): string {
  switch (s.type) {
    case 'home':
      return '';
    case 'my-tasks':
      return 'my';
    case 'uptime':
      return 'uptime';
    case 'deleted':
      return 'deleted';
    case 'completed':
      return 'completed';
    case 'contacts':
      return 'contacts';
    case 'pipelines':
      return 'pipelines';
    case 'client':
      return `c/${s.slug}`;
    case 'project':
      return `p/${s.slug}`;
    case 'task':
      return `t/${s.id}`;
  }
}

function parseHash(): Selection {
  const hash = window.location.hash.replace(/^#\/?/, '');
  if (!hash) return { type: 'home' };
  if (hash === 'my') return { type: 'my-tasks' };
  if (hash === 'uptime') return { type: 'uptime' };
  if (hash === 'deleted') return { type: 'deleted' };
  if (hash === 'completed') return { type: 'completed' };
  if (hash === 'contacts') return { type: 'contacts' };
  if (hash === 'pipelines') return { type: 'pipelines' };
  if (hash.startsWith('c/')) return { type: 'client', slug: hash.slice(2) };
  if (hash.startsWith('p/')) return { type: 'project', slug: hash.slice(2) };
  if (hash.startsWith('t/')) return { type: 'task', id: hash.slice(2) };
  return { type: 'home' };
}

export function SelectionProvider({ children }: { children: React.ReactNode }) {
  const [selection, setSelection] = useState<Selection>(() => parseHash());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set<string>());
  const [activityOpen, setActivityOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const onHash = () => setSelection(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const expandChain = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.add(k);
      return next;
    });
  }, []);

  const select = useCallback((s: Selection) => {
    window.location.hash = `#/${serialize(s)}`;
    // Auto-expand the parent chain so the sidebar tree "opens to the side"
    // as the user drills deeper. A client select expands the client; a
    // project select still needs the client expanded too; a task select
    // needs the full chain. The Sidebar uses data context to walk the
    // parent chain, so here we just expand what we know directly.
    if (s.type === 'client') {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(`client:${s.slug}`);
        return next;
      });
    }
  }, []);

  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const isExpanded = useCallback((key: string) => expanded.has(key), [expanded]);

  const toggleActivity = useCallback(() => setActivityOpen((v) => !v), []);

  const toggleShowArchived = useCallback(() => setShowArchived((v) => !v), []);

  return (
    <SelectionContext.Provider
      value={{
        selection,
        select,
        expanded,
        toggleExpanded,
        isExpanded,
        expandChain,
        activityOpen,
        toggleActivity,
        showArchived,
        toggleShowArchived,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
}

export function useSelection(): SelectionState {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error('useSelection must be used inside SelectionProvider');
  return ctx;
}
